import { FC, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'components/commercetools-ui/atoms/link';
import { highlightMatch } from 'components/commercetools-ui/atoms/search/helpers';
import { useProductList } from 'components/commercetools-ui/organisms/product/product-list/context';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import { mapProductToGA4Item, trackAddToWishlist as trackGA4AddToWishlist } from 'helpers/analytics/ga4Events';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { getAttributeLabel } from 'helpers/utils/attributes';
import { desktop } from 'helpers/utils/screensizes';
import { cleanString, hasDiscount } from 'helpers/utils/util';
import { useFeatures } from 'providers/feature-flag';
import { Variant } from 'types/entity/product';
import { LineItem } from 'types/entity/wishlist';
import { useProjectSettings } from 'frontastic';
import ColorSwatches from './components/color-swatches';
import HexColorSwatches from './components/hex-color-swatches';
import ProductCardImageCarousel from './components/product-card-image-carousel';
import ColoredTag from './components/tag';
import {
  buildProductUrl,
  buildWishlistLineItem,
  filterActivePrices,
  getFirstAvailableVariant,
  getMinimumPriceProduct,
  getP2rRetailerPrefixes,
  getUniqueTags,
  hasDuplicatePrice,
  resolveVariantIndex,
} from './helpers';
import Prices from './prices';
import { ProductTileProps } from './types';

const QuickView = dynamic(() => import('components/commercetools-ui/organisms/product/product-quick-view'), {
  ssr: false,
});

const COLOR_SWATCH_ATTRIBUTES = ['color_dd_v1', 'frame_color_wa'];

const getDiscountedPrice = (product: any, p2rRetailerPrefixes?: string[]) => {
  const minPriceProduct = getMinimumPriceProduct(product, p2rRetailerPrefixes);
  const activePrices = p2rRetailerPrefixes
    ? filterActivePrices(minPriceProduct?.prices, p2rRetailerPrefixes)
    : (minPriceProduct?.prices ?? []);
  const discountPrices = [
    ...(minPriceProduct?.discounted?.value ? [minPriceProduct.discounted?.value] : []),
    ...activePrices.map((p: any) => p?.discounted?.value).filter((v: any) => v?.centAmount != null),
  ];

  if (discountPrices.length === 0) return undefined;
  return discountPrices.reduce((min, curr) => (curr.centAmount < min.centAmount ? curr : min), discountPrices[0]);
};

const getMinPrice = (product: any, p2rRetailerPrefixes?: string[]) => {
  const minPriceProduct = getMinimumPriceProduct(product, p2rRetailerPrefixes);
  const activePrices = p2rRetailerPrefixes
    ? filterActivePrices(minPriceProduct?.prices, p2rRetailerPrefixes)
    : (minPriceProduct?.prices ?? []);
  const prices = [
    ...(minPriceProduct?.price ? [minPriceProduct.price] : []),
    ...activePrices.map((p: any) => p?.value).filter((v: any) => v?.centAmount != null),
  ];

  if (prices.length === 0) return undefined;
  return prices.reduce((min, curr) => (curr.centAmount < min.centAmount ? curr : min), prices[0]);
};

const getUniqueHexCodes = (variants: any[]): string[] => {
  return [...new Set(variants.flatMap((v) => v.attributes?.hexcodes ?? []))];
};

const ProductTile: FC<ProductTileProps> = ({
  product,
  isSearchResult = false,
  disableQuickView = false,
  disableWishlistButton = false,
  wishlist,
  selectedVariantIndex = 0,
  shippingMethods,
  onClick,
  addToWishlist,
  removeLineItem,
  onAddToCart,
  tileCTA,
  categories,

  textConfig,
  index,
  tieredPrice,
  calendarOptions,
  ffid,
  showCategory = false,
  isFavoritePage = false,
  isToggleP2r,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const { searchQuery } = useProductList();
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);
  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;

  const p2rRetailerPrefixes = useMemo(() => getP2rRetailerPrefixes(shippingMethods), [shippingMethods]);
  const minPriceProduct = getMinimumPriceProduct(product, p2rRetailerPrefixes);

  const discountedPrice = getDiscountedPrice(product, p2rRetailerPrefixes);
  const minPrice = getMinPrice(product, p2rRetailerPrefixes);
  const uniqueHexCodes = getUniqueHexCodes(product.variants);

  const variantIndex = resolveVariantIndex(product, minPriceProduct?.id, selectedVariantIndex);
  const firstAvailableVariant = getFirstAvailableVariant(product?.variants);

  const [selectedVariant, setSelectedVariant] = useState<Variant>(
    product?.variants[selectedVariantIndex] ?? firstAvailableVariant ?? variantWithDiscount,
  );

  const showColorSwatches =
    product?.variants.some((variant) =>
      COLOR_SWATCH_ATTRIBUTES.some((attribute) => getAttributeLabel(variant.attributes, attribute)),
    ) ?? false;
  const hasSamePrice = hasDuplicatePrice(product?.variants);
  const hasVariants = product?.variants.length > 1;

  const productToWishlistLineItem = useMemo(
    () => (selectedVariant ? buildWishlistLineItem(product, selectedVariant) : undefined),
    [product, selectedVariant],
  );

  const { enableGA4 } = useFeatures();

  const handleWishlistAdd = async (lineItem: LineItem, count: number) => {
    const currency = selectedVariant?.price?.currencyCode ?? 'USD';
    trackGA4AddToWishlist(currency, [mapProductToGA4Item(enableGA4(), product)]);
    await addToWishlist?.(lineItem, count);
  };

  const productUrl = useMemo(
    () => buildProductUrl(product, selectedVariant?.sku, isSearchResult, isToggleP2r),
    [product, selectedVariant, isSearchResult, isToggleP2r],
  );

  const shortDesc = product?.variants?.[variantIndex]?.attributes?.short_description_universal;

  const { projectSettings } = useProjectSettings();
  const uniqueTags = getUniqueTags((selectedVariant as { tags?: string[] })?.tags);
  const hasSaleDiscount = hasDiscount(selectedVariant);
  const visibleTags = uniqueTags?.slice(0, hasSaleDiscount ? 1 : 2);
  const PLACEHOLDER_IMAGE = projectSettings?.placeHolderImage ?? '/images/placeholder.jpg';

  useEffect(() => {
    const nextVariant =
      product?.variants?.length === 1
        ? product.variants[0]
        : (product?.variants[selectedVariantIndex] ?? firstAvailableVariant ?? variantWithDiscount);

    setSelectedVariant(nextVariant);
  }, [product, variantIndex, variantWithDiscount, firstAvailableVariant, selectedVariantIndex]);

  return (
    <div
      data-testid="product-tile"
      data-ffid={`ctr-product-tile${ffid ? '-' + ffid : ''}${index ?? ''}`}
      onMouseEnter={() => setHasInteracted(true)}
      onFocus={() => setHasInteracted(true)}
      className="group/tile relative flex size-full flex-col rounded-xl border border-solid border-[#EBEBEB] bg-white p-11 transition-all duration-300 ease-in-out dark:bg-gmsb-header-gray sm:p-15 lg:hover:h-[calc(100%+24px)] lg:hover:-translate-y-12 lg:hover:shadow-[0_0_8px_0_rgba(0,0,0,0.15)] lg:has-[:focus-visible]:h-[calc(100%+24px)] lg:has-[:focus-visible]:-translate-y-12 lg:has-[:focus-visible]:shadow-[0_0_8px_0_rgba(0,0,0,0.15)]"
    >
      <div
        className="h-24 self-end sm:h-28"
        data-ffid={`ctr-product-tile-wishlist-colortag${ffid ? '-' + ffid : ''}${index ?? ''}`}
      >
        {!disableWishlistButton && productToWishlistLineItem && (
          <WishlistButton
            data={wishlist}
            lineItem={productToWishlistLineItem}
            addToWishlist={handleWishlistAdd}
            removeFromWishlist={removeLineItem}
            productName={product?.name}
            ffid={ffid}
            forPLP
            isFavoritePage={isFavoritePage}
            shortDesc={shortDesc}
          />
        )}
      </div>
      {selectedVariant && (
        <div className="absolute z-[23] flex flex-col gap-y-4">
          {(hasSaleDiscount || visibleTags?.length > 0) && (
            <>
              {hasSaleDiscount && (
                <ColoredTag
                  isSale
                  dataFfid={`lbl-${ffid ? ffid + '-' : ''}product-sale-tag-${cleanString(product?.name ?? '')}`}
                />
              )}

              {visibleTags?.map((item, ind) => (
                <ColoredTag
                  key={`color-${item}-${ind}`}
                  label={item}
                  dataFfid={`lbl-${ffid ? ffid + '-' : ''}product-tag-${cleanString(product?.name ?? item)}-${ind}`}
                />
              ))}
            </>
          )}
        </div>
      )}
      <div className="relative">
        <Link
          onClick={onClick}
          link={productUrl}
          className="absolute inset-0 z-10"
          data-ffid={`lnk-${ffid ? ffid + '-' : ''}product-card-${cleanString(product?.name ?? '')}`}
          aria-label={`${product?.attributes?.design_name_universal ?? product?.name}, ${showCategory ? product?.categories?.[0]?.name : ''}`}
        ></Link>

        <div className="relative w-full cursor-pointer" data-testid="image-container">
          <ProductCardImageCarousel
            images={
              selectedVariant?.images && selectedVariant?.images?.length > 0
                ? selectedVariant?.images
                : [PLACEHOLDER_IMAGE]
            }
            alt={product?.name ?? ''}
            ffid={ffid}
            uniqueFFIDText={`product-card-${cleanString(product?.name ?? '')}`}
            classNames="my-8"
            onClick={onClick}
            url={productUrl}
            productTileIndex={index}
          />
        </div>

        <div className="relative mt-8 flex max-w-216 justify-center sm:mx-auto">
          <div
            data-ffid={`ctr-tile-${ffid ? ffid + '-' : ''}product-colors-${cleanString(product?.name ?? '')}`}
            className={'relative z-20 flex h-30 items-center gap-4 sm:gap-12'}
          >
            {showColorSwatches && (
              <ColorSwatches
                product={product}
                selectedVariant={selectedVariant}
                currentVariantUpdate={(variant) => setSelectedVariant(variant)}
                attributesToBeConsidered={COLOR_SWATCH_ATTRIBUTES}
              />
            )}
            {!showColorSwatches && uniqueHexCodes.length > 0 && (
              <HexColorSwatches hexCodes={uniqueHexCodes} ffid={ffid} productName={product?.name} />
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-col text-center">
          <h4
            title={product?.name}
            className="truncate text-14 font-bold sm:text-16"
            data-ffid={`lbl-${ffid ? ffid + '-' : ''}product-name-${cleanString(product?.name ?? '')}`}
          >
            {product?.attributes?.design_name_universal ?? product?.name}
          </h4>

          {showCategory && (
            <p
              title={product?.categories?.[0]?.name}
              className="truncate text-12 sm:text-14"
              data-ffid={`lbl-${ffid ? ffid + '-' : ''}product-category-${cleanString(product?.categories?.[0]?.name ?? '')}`}
            >
              {product?.categories?.[0]?.name}
            </p>
          )}

          {shortDesc && (
            <p
              title={product?.attributes?.short_description_universal}
              className="truncate text-12 sm:text-14"
              data-ffid={`lbl-${ffid ? ffid + '-' : ''}product-category-${cleanString(product?.name ?? '')}`}
            >
              {highlightMatch(shortDesc ?? '', searchQuery ?? '', 'bg-[#FFFF00]')}
            </p>
          )}

          <Prices
            ffid={ffid}
            minPrice={minPrice}
            textConfig={textConfig}
            discountedPrice={discountedPrice}
            hasVariants={hasVariants}
            hasSamePrice={hasSamePrice}
            productName={product?.name}
          />
        </div>
      </div>
      {!disableQuickView && hasInteracted && (
        <div className="mt-auto flex h-0 flex-col-reverse items-center opacity-0 lg:transition-opacity lg:duration-300 lg:ease-in-out lg:group-hover/tile:opacity-100 lg:group-has-[:focus-visible]/tile:opacity-100">
          <QuickView
            className="flex-none"
            buttonIsEnabled={isDesktopSize}
            product={product}
            wishlist={wishlist}
            addToWishlist={handleWishlistAdd}
            removeFromWishlist={removeLineItem}
            onAddToCart={async (variant, quantity: number) => {
              await onAddToCart?.(variant, quantity);
            }}
            tileCTA={tileCTA}
            categories={categories}
            textConfig={textConfig}
            tieredPrice={tieredPrice}
            calendarOptions={calendarOptions}
            shortDesc={shortDesc}
            selectedVariantSKU={selectedVariant?.sku}
          />
        </div>
      )}
    </div>
  );
};

export default ProductTile;
